import * as React from "react";
import { Toast, ToastViewport } from "./toast";

/**
 * Simple global store so <Toaster/> can render toasts triggered anywhere.
 * Hook usage:
 *   const { toast } = useToastStore();
 *   toast({ title: "Saved", description: "Settings updated", variant: "success" });
 */
type ToastData = {
  id: string;
  title: string;
  description: string;
  variant: "default" | "success" | "error" | "warning";
};

// `toast` är både anropbar (toast({...})) OCH har bekvämlighetsmetoder
// (toast.error('...') m.fl.). ~37 anropsställen använde .error/.success/.info
// som INTE fanns → TypeError vid felhantering. Metoderna finns nu.
type ToastFn = ((t: Omit<ToastData, "id">) => string) & {
  error: (msg: string) => string;
  success: (msg: string) => string;
  warning: (msg: string) => string;
  info: (msg: string) => string;
};

type Store = {
  toasts: ToastData[];
  subscribers: Set<() => void>;
  add: (t: Omit<ToastData, "id">) => string;
  dismiss: (id: string) => void;
  clear: () => void;
};

const store: Store = {
  toasts: [],
  subscribers: new Set(),
  add: (t) => {
    const id = crypto.randomUUID();
    store.toasts.push({ id, ...t });
    store.subscribers.forEach((s) => s());
    return id;
  },
  dismiss: (id) => {
    store.toasts = store.toasts.filter((t) => t.id !== id);
    store.subscribers.forEach((s) => s());
  },
  clear: () => {
    store.toasts = [];
    store.subscribers.forEach((s) => s());
  },
};

export function useToastStore() {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const sub = () => setTick((n) => n + 1);
    store.subscribers.add(sub);
    return () => {
      store.subscribers.delete(sub);
    };
  }, []);
  // Stabil identitet er et API-krav: `toast` bruges i useCallback/useEffect-deps
  // rundt om i appen — en ny funktion per render giver uendelige fetch-loops
  // (VikarDirectory, TimerArkiv: evig "Indlæser...").
  const api = React.useMemo(() => {
    const toast = ((t: Omit<ToastData, "id">) => store.add(t)) as ToastFn;
    toast.error = (msg: string) => store.add({ title: msg, description: "", variant: "error" });
    toast.success = (msg: string) => store.add({ title: msg, description: "", variant: "success" });
    toast.warning = (msg: string) => store.add({ title: msg, description: "", variant: "warning" });
    toast.info = (msg: string) => store.add({ title: msg, description: "", variant: "default" });
    return {
      toast,
      dismiss: (id: string) => store.dismiss(id),
      clear: () => store.clear(),
    };
  }, []);
  return { toasts: store.toasts, ...api };
}

export function Toaster() {
  const { toasts, dismiss } = useToastStore();

  return (
    <ToastViewport>
      {toasts.map((t) => (
        <Toast
          key={t.id}
          title={t.title}
          description={t.description}
          variant={t.variant}
          action={
            <button
              onClick={() => dismiss(t.id)}
              className="text-sm opacity-70 hover:opacity-100 underline"
            >
              Luk
            </button>
          }
        />
      ))}
    </ToastViewport>
  );
}



